<template>
  <div class="dy-discovery mp-page">
    <header class="page-hero">
      <div class="hero-actions">
        <el-button @click="$router.push({ path: '/discover/library', query: { platform: 'douyin' } })">发现库</el-button>
        <el-button :icon="Refresh" :loading="loadingAccounts" @click="loadAccounts">刷新账号</el-button>
      </div>
    </header>

    <section class="mp-panel">
      <div class="mp-panel-body">
        <el-form @submit.prevent="runSearch(0, false)">
          <el-row :gutter="12">
            <el-col :span="6">
              <div class="field-label">搜索账号</div>
              <el-select v-model="accountId" placeholder="选择抖音账号" filterable style="width: 100%">
                <el-option
                  v-for="a in dyAccounts"
                  :key="a.account_id"
                  :label="`${a.display_name || a.account_id}`"
                  :value="a.account_id"
                />
              </el-select>
            </el-col>
            <el-col :span="10">
              <div class="field-label">关键词</div>
              <el-input v-model="keyword" placeholder="输入搜索词" @keyup.enter="runSearch(0, false)">
                <template #append>
                  <el-button type="primary" :icon="Search" :loading="searching" @click="runSearch(0, false)">搜索</el-button>
                </template>
              </el-input>
            </el-col>
            <el-col :span="4">
              <div class="field-label">排序</div>
              <el-select v-model="filters.sort_type" style="width: 100%">
                <el-option label="综合" value="0" />
                <el-option label="最多点赞" value="1" />
                <el-option label="最新发布" value="2" />
              </el-select>
            </el-col>
            <el-col :span="4">
              <div class="field-label">发布时间</div>
              <el-select v-model="filters.publish_time" style="width: 100%">
                <el-option label="不限" value="0" />
                <el-option label="一天内" value="1" />
                <el-option label="一周内" value="7" />
                <el-option label="半年内" value="180" />
              </el-select>
            </el-col>
          </el-row>
          <el-row :gutter="12" style="margin-top: 12px">
            <el-col :span="14">
              <div class="field-label">视频链接 / ID</div>
              <el-input v-model="videoUrl" placeholder="https://www.douyin.com/video/... 或短链" />
            </el-col>
            <el-col :span="4">
              <div class="field-label">&nbsp;</div>
              <el-button :loading="fetchingUrl" :disabled="!dyAccounts.length" @click="fetchByUrl">URL 直查</el-button>
            </el-col>
          </el-row>
        </el-form>
        <el-alert v-if="error" :title="error" type="error" show-icon closable style="margin-top: 12px" @close="error = ''" />
        <el-empty v-if="!loadingAccounts && !dyAccounts.length" description="还没有可用的抖音账号">
          <el-button type="primary" @click="$router.push('/publish')">去绑定账号</el-button>
        </el-empty>
      </div>
    </section>

    <section class="mp-panel">
      <div class="mp-panel-header">
        <span class="mp-panel-title">{{ searchedKeyword ? `"${searchedKeyword}" 的结果` : '搜索结果' }}</span>
        <span class="mp-muted">{{ notes.length }} 条</span>
      </div>
      <div class="mp-panel-body">
        <el-empty v-if="!notes.length" :description="searchedKeyword ? '没有结果' : '输入关键词后开始搜索'" />
        <el-row v-else :gutter="14">
          <el-col v-for="note in notes" :key="note.note_id" :xs="12" :sm="8" :md="6" :lg="4">
            <div class="note-card" @click="openDetail(note)">
              <div class="cover-wrap">
                <el-image v-if="note.cover_url" :src="note.cover_url" fit="cover" class="cover" referrerpolicy="no-referrer" />
                <div v-else class="cover cover--empty">无封面</div>
                <el-tag size="small" class="kind-tag" type="danger">视频</el-tag>
              </div>
              <div class="note-title">{{ note.title || '未命名' }}</div>
              <div class="note-meta">{{ note.author_name || '未知作者' }}</div>
              <div class="note-stats">
                <span>播 {{ note.views || 0 }}</span>
                <span>赞 {{ note.likes || 0 }}</span>
                <span>评 {{ note.comments || 0 }}</span>
              </div>
              <div class="note-actions" @click.stop>
                <el-button
                  size="small"
                  :type="savedIds.has(note.note_id) ? 'info' : 'primary'"
                  :loading="savingIds.has(note.note_id)"
                  :disabled="savedIds.has(note.note_id)"
                  @click="saveNote(note)"
                >{{ savedIds.has(note.note_id) ? '已保存' : '保存' }}</el-button>
                <el-button size="small" :loading="loadingCommentIds.has(note.note_id)" @click="toggleComments(note)">
                  {{ commentMap[note.note_id] ? '收起' : '评论' }}
                </el-button>
                <el-link v-if="note.note_url" :href="note.note_url" target="_blank" type="primary">原文</el-link>
              </div>
              <div v-if="commentMap[note.note_id]" class="comment-preview" @click.stop>
                <div v-if="!commentMap[note.note_id].length" class="muted">暂无评论</div>
                <div v-for="c in commentMap[note.note_id].slice(0, 3)" :key="c.comment_id" class="comment-line">
                  <strong>{{ c.user_name }}</strong> {{ c.content }}
                </div>
              </div>
            </div>
          </el-col>
        </el-row>
        <div v-if="notes.length" class="load-more">
          <el-button :loading="loadingMore" :disabled="!hasMore" @click="loadMore">
            {{ hasMore ? '加载更多' : '没有更多了' }}
          </el-button>
        </div>
      </div>
    </section>

    <el-drawer
      v-model="drawerVisible"
      :title="selectedNote?.title || '作品详情'"
      size="420px"
      class="platform-preview-drawer"
    >
      <PlatformContentViewer
        v-if="viewerItem"
        :item="viewerItem"
        :loading="loadingDetail"
      />
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'
import axios from 'axios'
import { PUBLISH_V2 } from '../utils/appBase'
import { publishV2Api } from '../api'
import PlatformContentViewer from '../components/platform/PlatformContentViewer.vue'
import { normalizeContent } from '../components/platform/normalizeContent'

const V2 = PUBLISH_V2

const accounts = ref([])
const accountId = ref('')
const keyword = ref('')
const videoUrl = ref('')
const filters = reactive({ sort_type: '0', publish_time: '0', content_type: '0' })
const notes = ref([])
const offset = ref(0)
const hasMore = ref(false)
const searchedKeyword = ref('')
const error = ref('')
const loadingAccounts = ref(false)
const searching = ref(false)
const loadingMore = ref(false)
const fetchingUrl = ref(false)
const savedIds = ref(new Set())
const savingIds = ref(new Set())
const commentMap = ref({})
const loadingCommentIds = ref(new Set())
const drawerVisible = ref(false)
const selectedNote = ref(null)
const loadingDetail = ref(false)

const dyAccounts = computed(() =>
  accounts.value.filter((a) => a.platform === 'douyin' && (a.has_cookie || a.login_status === 'logged_in')),
)

const viewerItem = computed(() => {
  if (!selectedNote.value) return null
  return normalizeContent(
    { ...selectedNote.value, platform: 'douyin' },
    { platform: 'douyin', proxyUrl: publishV2Api.coverProxyUrl },
  )
})

const loadAccounts = async () => {
  loadingAccounts.value = true
  try {
    const res = await axios.get(`${V2}/accounts`)
    accounts.value = res.data.items || []
    if (!accountId.value && dyAccounts.value.length) {
      accountId.value = dyAccounts.value[0].account_id
    }
  } finally {
    loadingAccounts.value = false
  }
}

const loadSavedIds = async () => {
  try {
    const res = await axios.get(`${V2}/douyin/discovery/saved-note-ids`)
    savedIds.value = new Set(res.data.items || [])
  } catch { /* ignore */ }
}

const runSearch = async (nextOffset, append) => {
  if (!accountId.value) {
    error.value = '请先选择抖音账号'
    return
  }
  if (!keyword.value.trim()) {
    error.value = '请输入关键词'
    return
  }
  error.value = ''
  append ? (loadingMore.value = true) : (searching.value = true)
  try {
    const res = await axios.post(`${V2}/douyin/discovery/search`, {
      account_id: accountId.value,
      keyword: keyword.value.trim(),
      offset: nextOffset,
      ...filters,
    }, { timeout: 45000 })
    const items = res.data.items || []
    notes.value = append ? [...notes.value, ...items] : items
    offset.value = res.data.next_offset || nextOffset
    hasMore.value = !!res.data.has_more
    searchedKeyword.value = keyword.value.trim()
    if (!append) commentMap.value = {}
  } catch (e) {
    error.value = e.response?.data?.detail || '搜索失败'
  } finally {
    searching.value = false
    loadingMore.value = false
  }
}

const loadMore = () => runSearch(offset.value, true)

const fetchByUrl = async () => {
  if (!accountId.value || !videoUrl.value.trim()) {
    error.value = '请选择账号并输入链接'
    return
  }
  fetchingUrl.value = true
  error.value = ''
  try {
    const res = await axios.post(`${V2}/douyin/discovery/video-detail`, {
      account_id: accountId.value,
      url: videoUrl.value.trim(),
    }, { timeout: 45000 })
    notes.value = [res.data]
    hasMore.value = false
    searchedKeyword.value = 'URL 直查'
    offset.value = 0
  } catch (e) {
    error.value = e.response?.data?.detail || 'URL 直查失败'
  } finally {
    fetchingUrl.value = false
  }
}

const openDetail = async (note) => {
  selectedNote.value = note
  drawerVisible.value = true
  if (note.content && note.views != null) return
  loadingDetail.value = true
  try {
    const res = await axios.post(`${V2}/douyin/discovery/video-detail`, {
      account_id: accountId.value,
      url: note.note_url || note.note_id,
    }, { timeout: 45000 })
    selectedNote.value = { ...note, ...res.data }
  } catch (e) {
    ElMessage.warning(e.response?.data?.detail || '详情补充失败，已显示列表信息')
  } finally {
    loadingDetail.value = false
  }
}

const saveNote = async (note) => {
  if (!note?.note_id) return
  savingIds.value = new Set([...savingIds.value, note.note_id])
  try {
    const res = await axios.post(`${V2}/douyin/discovery/save`, {
      note,
      account_id: accountId.value,
      source_keyword: searchedKeyword.value,
      comments: commentMap.value[note.note_id] || undefined,
    }, { timeout: 20000 })
    savedIds.value = new Set([...savedIds.value, note.note_id])
    ElMessage.success(res.data.duplicate ? '已在发现库' : '已保存到发现库')
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '保存失败')
  } finally {
    const s = new Set(savingIds.value)
    s.delete(note.note_id)
    savingIds.value = s
  }
}

const toggleComments = async (note) => {
  if (commentMap.value[note.note_id]) {
    const m = { ...commentMap.value }
    delete m[note.note_id]
    commentMap.value = m
    return
  }
  loadingCommentIds.value = new Set([...loadingCommentIds.value, note.note_id])
  try {
    const res = await axios.post(`${V2}/douyin/discovery/video-comments`, {
      account_id: accountId.value,
      aweme_id: note.note_id,
    }, { timeout: 30000 })
    commentMap.value = { ...commentMap.value, [note.note_id]: res.data.items || [] }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '评论拉取失败')
  } finally {
    const s = new Set(loadingCommentIds.value)
    s.delete(note.note_id)
    loadingCommentIds.value = s
  }
}

onMounted(async () => {
  await loadAccounts()
  await loadSavedIds()
})
</script>

<style scoped>
.page-hero {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 16px;
}
.hero-title {
  font-size: 22px;
  font-weight: 650;
  letter-spacing: -0.03em;
  color: var(--mp-text);
}
.hero-desc {
  margin-top: 6px;
  font-size: 13.5px;
  color: var(--mp-text-muted);
}
.hero-actions { display: flex; gap: 8px; }
.field-label {
  font-size: 12px;
  color: var(--mp-text-muted);
  margin-bottom: 6px;
}
.note-card {
  border: 1px solid var(--mp-border);
  border-radius: var(--mp-radius-sm);
  padding: 10px;
  margin-bottom: 14px;
  cursor: pointer;
  background: var(--mp-surface);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.note-card:hover {
  border-color: #c9d8f8;
  box-shadow: var(--mp-shadow-sm);
}
.cover-wrap { position: relative; margin-bottom: 8px; }
.cover {
  width: 100%;
  height: 160px;
  border-radius: 8px;
  background: var(--mp-surface-muted);
}
.cover--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mp-text-muted);
  font-size: 13px;
}
.kind-tag { position: absolute; left: 8px; top: 8px; }
.note-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--mp-text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 36px;
}
.note-meta { margin-top: 4px; font-size: 12px; color: var(--mp-text-muted); }
.note-stats {
  margin-top: 6px;
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: var(--mp-text-secondary);
}
.note-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.comment-preview {
  margin-top: 8px;
  padding: 8px;
  background: var(--mp-surface-muted);
  border-radius: 8px;
  font-size: 12px;
}
.comment-line { margin-bottom: 4px; color: var(--mp-text-secondary); }
.load-more { margin-top: 8px; text-align: center; }
.muted { color: var(--mp-text-muted); }
</style>
